import moment from "moment";
import { observer } from "mobx-react-lite";
import { connect, registerHandler } from "../../utils/socket";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";

import React, { useCallback, useEffect, useState } from "react";
import {
  getUserIds,
  getAggregationHeartRate,
  getAggregationRRIntervals,
  getAggregationSDNN,
  getAggregationSDNNStats,
  getAggregationStatusColor,
  getAggregationStatusLabel,
} from "../../utils/api";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import CalendarFilter from "../chart/CalendarFilter";
import { useSearchParams } from "react-router-dom";
import Stress from "../chart/Stress";
import Stress2 from "../chart/Stress2";

import "./Dashboard.css";

function Dashboard({ store, navStore, authStore, dashboardStore }) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [userid, setUserid] = useState(searchParams.get("userId"));
  const [userIds, setUserIds] = useState([]);

  const [endDate, setEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [heartRates, setHeartRates] = useState([]);
  const [statusColor, setStatusColor] = useState([]);
  const [groupStatusColor, setGroupStatusColor] = useState([]);
  const [statusLabel, setStatusLabel] = useState([]);

  const [rrIntervals, setRRIntervals] = useState([]);
  const [sdnn, setSdnn] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [yellowHours, setYellowHours] = useState([]);
  const [redHours, setRedHours] = useState([]);
  const [greenHours, setGreenHours] = useState([]);

  const [headerChange, setHeaderChange] = useState(false);
  const [filterChange, setFilterChange] = useState(false);

  const [aggregration, setAggregration] = useState("daily");
  const [datesWithData, setDatesWithData] = useState([]);
  const [hoursWithData, setHoursWithData] = useState([]);
  const [lastHeartRate, setLastHeartRate] = useState(null);
  const [lastSdnn, setLastSdnn] = useState(null);
  const navigate = useNavigate();

  const merge = (old, current, key = "end_date") => {
    const accum = {};
    old.forEach((x) => {
      accum[x[key]] = x;
    });
    current.forEach((x) => {
      accum[x[key]] = x;
    });
    const sortedKeys = Object.keys(accum);
    sortedKeys.sort();
    const newList = [];
    sortedKeys.forEach((x) => {
      if (
        dashboardStore.filterStartDate !== "" &&
        dashboardStore.filterEndDate !== "" &&
        (moment(x) < moment(dashboardStore.filterStartDate) ||
          moment(x) > moment(dashboardStore.filterEndDate))
      ) {
        return;
      }
      newList.push(accum[x]);
    });
    return newList;
  };

  const mergeStatus = (old, current) => {
    if (old.length <= 0) {
      return old;
    }
    const toReturn = [...old];
    const lastKey = toReturn[toReturn.length - 1];
    if (moment(lastKey[1].timestamp) >= moment(current.timestamp)) {
      return toReturn;
    } else {
      const newValue = current.color;
      const newTimestamp = current.timestamp;
      if (lastKey[1].value === newValue) {
        lastKey[1].timestamp = newTimestamp;
      } else {
        toReturn.push([
          { value: newValue, timestamp: newTimestamp, start: true },
          { value: newValue, timestamp: newTimestamp, start: false },
        ]);
      }
    }
    return toReturn;
  };

  const socketMessageReceived = (data) => {
    if (!dashboardStore.isRealtime) {
      return;
    }
    if (data.hasOwnProperty("bars")) {
      const bars = data["bars"];
      if (bars.hasOwnProperty("heartrate")) {
        dashboardStore.setFilteredHeartRates(
          merge(
            dashboardStore.filteredHeartRates,
            bars["heartrate"],
            "timepoint"
          )
        );
      }
      if (bars.hasOwnProperty("rrintervals")) {
        dashboardStore.setFilteredRRIntervals(
          merge(
            dashboardStore.filteredRRIntervals,
            bars["rrintervals"],
            "timepoint"
          )
        );
      }
      if (bars.hasOwnProperty("sdnn")) {
        dashboardStore.setFilteredSdnn(
          merge(dashboardStore.filteredSdnn, bars["sdnn"], "timepoint")
        );
      }
    }
    if (data.hasOwnProperty("stats")) {
      const stats = data["stats"];
      if (stats.hasOwnProperty("sdnn")) {
        dashboardStore.setFilteredStatsData(
          merge(dashboardStore.filteredStatsData, stats["sdnn"])
        );
      }
    }

    if (data.hasOwnProperty("status")) {
      dashboardStore.setFilteredStatus(
        mergeStatus(dashboardStore.filteredStatus, data["status"])
      );
    }
    if (data.hasOwnProperty("heartRates")) {
      const hr = data["heartRates"][data["heartRates"].length - 1];
      setLastHeartRate(hr.heartRate);
    }
    if (data.hasOwnProperty("metrics")) {
      const metrics = data["metrics"][data["metrics"].length - 1];
      setLastSdnn(metrics.sdnn);
    }
  };

  useEffect(() => {
    if (!userid) {
      return;
    }
    connect();
    registerHandler(socketMessageReceived);
    setTimeout(() => {
      dashboardStore.addSubscription(userid);
    }, 2000);
  }, [userid]);

  const activeDateMapping = (active, date, headerChange) => {
    let interval = 1;
    if (active === "day") interval = 1;
    if (active === "week") interval = 6;
    if (active === "month") interval = 24;

    dashboardStore.setIsRealtime(
      moment(date).add(1, "hour") > moment(new Date())
    );
    let startDate;
    let endDate;
    if (!headerChange) {
      endDate = moment(date).add(interval, "hours").toDate().toISOString();
      startDate = moment(date).toDate().toISOString();
      dashboardStore.setFilterStartDate(startDate);
      dashboardStore.setFilterEndDate(endDate);
      setFilterChange(true);
    } else {
      dashboardStore.setFilterStartDate("");
      dashboardStore.setFilterEndDate("");

      startDate = moment(date).startOf(active).toDate().toISOString();
      endDate = moment(date).endOf(active).toDate().toISOString();
      setStartDate(startDate);
      setEndDate(endDate);
      setAggregration(
        active === "month" ? "monthly" : active === "week" ? "weekly" : "daily"
      );
      setHeaderChange(true);
    }
  };

  const groupEm = (statusColor) => {
    let i = 1;
    const groupedStatusColor = [];
    let prev = null;
    statusColor.forEach((element) => {
      if (i % 2 === 0) {
        groupedStatusColor.push([prev, element]);
      }
      prev = element;
      i += 1;
    });
    return groupedStatusColor;
  };

  useEffect(() => {
    const getDatesWithData = async () => {
      let userids_ = await getUserIds();
      setUserIds(userids_.data);
      let timepoint;

      if (userid == null && userids_.data.length === 1) {
        setUserid(userids_.data[0].userid);
        timepoint = userids_.data[0];
      } else {
        timepoint = userids_.data.find((d) => d.userid === userid);
      }
      let isoStart = moment(timepoint.min_datetime).toDate().toISOString();
      let isoEnd = moment(timepoint.max_datetime).toDate().toISOString();
      let heartRates = await getAggregationHeartRate(
        userid,
        isoStart,
        isoEnd,
        "daily"
      );
      const dates = heartRates.map((x) => moment(x.timepoint));
      dates.sort();
      dates.reverse();

      const datesWithData = [];
      dates.forEach((x) => {
        let date = x.toDate().toISOString();
        if (
          !datesWithData.find(
            (date_) => date_.split("T")[0] === date.split("T")[0]
          )
        ) {
          datesWithData.push(date);
        }
      });
      datesWithData.sort();

      const hoursWithData = new Set();
      heartRates.forEach((x) => {
        let date = moment(x.timepoint).toDate().toISOString().substring(0, 13);
        hoursWithData.add(date);
      });

      setDatesWithData(datesWithData);
      setHoursWithData(hoursWithData);
      setEndDate(
        moment(datesWithData[datesWithData.length - 1])
          .endOf("day")
          .toDate()
          .toISOString()
      );
      setStartDate(
        moment(datesWithData[datesWithData.length - 1])
          .startOf("day")
          .toDate()
          .toISOString()
      );
      store.userids = userids_.data.map((x) => x.userid).sort();
      store.useridsWithDateRange = userids_.data;
      setHeaderChange(true);
    };

    getDatesWithData();
  }, [store, userid]);

  useEffect(() => {
    navStore.selectedKey = "1";

    const fetchMyApi = async () => {
      if (userid !== "" && startDate !== "" && endDate !== "" && headerChange) {
        setDataLoading(true);
        let isoStart = moment(startDate).toDate().toISOString();
        let isoEnd = moment(endDate).toDate().toISOString();
        let heartRate_ = getAggregationHeartRate(
          userid,
          isoStart,
          isoEnd,
          aggregration
        );
        let rrIntervals_ = getAggregationRRIntervals(
          userid,
          isoStart,
          isoEnd,
          aggregration
        );
        let statusColor_ = getAggregationStatusColor(
          userid,
          isoStart,
          isoEnd,
          aggregration
        );
        let statusLabel_ = getAggregationStatusLabel(
          userid,
          isoStart,
          isoEnd,
          aggregration
        );
        let sdnn_ = getAggregationSDNN(userid, isoStart, isoEnd, aggregration);
        let heartRate = await heartRate_;
        let sdnn = await sdnn_;
        let rrInterval = await rrIntervals_;
        let statusColor = await statusColor_;
        let statusLabel = await statusLabel_;
        setHeartRates(heartRate);
        dashboardStore.setFilteredHeartRates(heartRate);
        setRRIntervals(rrInterval);
        dashboardStore.setFilteredRRIntervals(rrInterval);
        setSdnn(sdnn);
        dashboardStore.setFilteredSdnn(sdnn);
        setStatusColor(statusColor);
        const groupStatusColor_ = groupEm(statusColor);
        setGroupStatusColor(groupStatusColor_);
        dashboardStore.setFilteredStatus(groupStatusColor_);
        setStatusLabel(statusLabel);
        setHeaderChange(false);
        setDataLoading(false);
        setLoading(false);
      }

      if (
        userid !== "" &&
        dashboardStore.filterStartDate !== "" &&
        dashboardStore.filterStartDate !== "" &&
        filterChange
      ) {
        const newHeartRates = heartRates.filter(
          (d) =>
            moment(d.timepoint).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
            moment(d.timepoint).utc() <=
              moment(dashboardStore.filterEndDate).utc()
        );
        dashboardStore.setFilteredHeartRates(newHeartRates);
        const newRRIntervals = rrIntervals.filter(
          (d) =>
            moment(d.timepoint).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
            moment(d.timepoint).utc() <=
              moment(dashboardStore.filterEndDate).utc()
        );
        dashboardStore.setFilteredRRIntervals(newRRIntervals);
        const newSdnn = sdnn.filter(
          (d) =>
            moment(d.timepoint).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
            moment(d.timepoint).utc() <=
              moment(dashboardStore.filterEndDate).utc()
        );
        dashboardStore.setFilteredSdnn(newSdnn);
        const newSdnnStats = statsData.filter(
          (d) =>
            moment(d.end_date).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
            moment(d.end_date).utc() <=
              moment(dashboardStore.filterEndDate).utc()
        );
        dashboardStore.setFilteredStatsData(newSdnnStats);

        const filteredGroupStatusColor = groupEm(statusColor).filter((grp) => {
          return (
            (moment(grp[1].timestamp).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
              moment(grp[1].timestamp).utc() <=
                moment(dashboardStore.filterEndDate).utc()) ||
            (moment(grp[0].timestamp).utc() >=
              moment(dashboardStore.filterStartDate).utc() &&
              moment(grp[0].timestamp).utc() <=
                moment(dashboardStore.filterEndDate).utc())
          );
        });
        dashboardStore.setFilteredStatus(filteredGroupStatusColor);

        setFilterChange(false);
      }
    };
    fetchMyApi();
  }, [
    userid,
    endDate,
    startDate,
    headerChange,
    store,
    navStore,
    searchParams,
    aggregration,
    heartRates,
    sdnn,
    dashboardStore.filterStartDate,
    dashboardStore.filterEndDate,
    filterChange,
    statsData,
  ]);

  useEffect(() => {
    const yellowHours_ = {};
    const greenHours_ = {};
    const redHours_ = {};

    groupStatusColor.forEach((grps) => {
      grps.forEach((grp) => {
        if (grp.value === "yellow") {
          yellowHours_[grp.timestamp.substring(0, 13)] = true;
        } else if (grp.value === "red") {
          redHours_[grp.timestamp.substring(0, 13)] = true;
        } else if (grp.value === "green") {
          greenHours_[grp.timestamp.substring(0, 13)] = true;
        }
      });
      setYellowHours(yellowHours_);
      setGreenHours(greenHours_);
      setRedHours(redHours_);
    });
  }, [groupStatusColor]);

  const fetchSdnnStats = useCallback(async () => {
    if (userid !== "" && startDate !== "" && endDate !== "" && headerChange) {
      const multiplier = 30;
      const isoStart = moment(startDate).toDate().toISOString();
      let isoEnd = moment(endDate).toDate().toISOString();
      if (isoStart === isoEnd) {
        isoEnd = moment(endDate).add(1, "days").toDate().toISOString();
      }
      let sdnnStats = await getAggregationSDNNStats(
        userid,
        isoStart,
        isoEnd,
        aggregration,
        multiplier
      );
      sdnnStats = sdnnStats.filter((x) => x.count >= 8);
      setStatsData(sdnnStats);
      dashboardStore.setFilteredStatsData(sdnnStats);
      setHeaderChange(false);
      setLoading(false);
    }
  }, [aggregration, endDate, headerChange, startDate, userid]);

  useEffect(() => {
    fetchSdnnStats();
  }, [fetchSdnnStats]);

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="Dashboard">
          <Button
            style={{ marginTop: "5px", marginRight: "5px", float: "right" }}
            onClick={() => navigate(`/detail-chart?userId=${userid}`)}
          >
            Timeseries View
          </Button>
          <CalendarFilter
            latestEndDate={endDate}
            setHeaderChange={setHeaderChange}
            userIds={userIds}
            defaultUserId={userid}
            setUserid={setUserid}
            datesWithData={datesWithData}
            hoursWithData={hoursWithData}
            activeDateMapping={activeDateMapping}
            yellowHours={yellowHours}
            redHours={redHours}
            greenHours={greenHours}
          />
          {dataLoading ? (
            <LoadingSpinner />
          ) : heartRates || sdnn ? (
            <div>
              {dashboardStore.filteredStatsData.length > 0 ? (
                <React.Fragment>
                  <h3 className="Stress_chart__heading">HRV</h3>
                  <Stress2
                    sdnnBars={dashboardStore.filteredSdnn}
                    sdnnStats={dashboardStore.filteredStatsData}
                    isRealtime={dashboardStore.isRealtime}
                    lastHeartRate={lastHeartRate}
                    lastSdnn={lastSdnn}
                    heartRateBars={dashboardStore.filteredHeartRates}
                    groupStatusColor={dashboardStore.filteredStatus}
                    statusLabel={statusLabel}
                  />
                </React.Fragment>
              ) : (
                <h3 className="Stress_chart__heading">No Data Availabe</h3>
              )}
              {dashboardStore.filteredHeartRates &&
              dashboardStore.filteredHeartRates.length > 0 ? (
                <React.Fragment>
                  <h3 className="Stress_chart__heading">Heart rate</h3>
                  <Stress
                    heartRateBars={dashboardStore.filteredHeartRates}
                    sdnnBars={sdnn}
                    sdnnStats={statsData}
                    isRealtime={dashboardStore.isRealtime}
                  />
                </React.Fragment>
              ) : (
                <></>
              )}
              {dashboardStore.filteredRRIntervals &&
              dashboardStore.filteredRRIntervals.length > 0 ? (
                <React.Fragment>
                  <h3 className="Stress_chart__heading">RR Interval</h3>
                  <Stress
                    heartRateBars={dashboardStore.filteredRRIntervals}
                    sdnnBars={sdnn}
                    sdnnStats={statsData}
                    isRealtime={dashboardStore.isRealtime}
                  />
                </React.Fragment>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
    </>
  );
}

export default observer(Dashboard);
